import { motion } from 'framer-motion'
import { redirectToRestaurants } from '../../config/navigation'

export default function Hero() {
  return (
    <>
      {/* Hero */}
      <section className="min-h-[calc(100vh-68px)] bg-gradient-to-br from-[#e8cc45] via-[#f5d96b] to-[#fff3b0] flex items-center px-4 py-16 relative overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[10%] right-[5%] w-96 h-96 rounded-full bg-white/30 blur-3xl" />
          <div className="absolute bottom-[10%] left-[5%] w-72 h-72 rounded-full bg-[#c9a800]/20 blur-3xl" />
        </div>

        <div className="container-wide relative">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Copy */}
            <motion.div
              initial={{ opacity: 0, x: -28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 bg-black/10 rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-widest uppercase mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-neutral-900 block" />
                Now available on the web
              </div>

              <h1 className="text-[clamp(2.8rem,5.5vw,5.2rem)] font-black leading-[1.0] tracking-[-0.03em] text-neutral-900 mb-6">
                Your favourite<br />
                restaurants,<br />
                <span className="relative inline-block">
                  delivered.
                  <span className="absolute bottom-[-4px] left-0 right-0 h-1 bg-neutral-900 rounded-sm" />
                </span>
              </h1>

              <p className="text-lg leading-relaxed text-black/60 max-w-md mb-10">
                Browse menus, customise your order and track delivery — all in your browser. No app download needed.
              </p>

              {/* CTAs */}
              <div className="flex items-center gap-4 flex-wrap mb-10">
                <motion.button
                  whileHover={{ y: -2, boxShadow: '0 8px 24px rgba(0,0,0,0.22)' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => redirectToRestaurants()}
                  className="inline-flex items-center gap-2.5 bg-neutral-900 text-white px-8 py-3.5 rounded-[14px] text-base font-bold shadow-lg transition-shadow"
                >
                  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 2 3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
                    <line x1="3" y1="6" x2="21" y2="6"/>
                    <path d="M16 10a4 4 0 01-8 0"/>
                  </svg>
                  Start Ordering
                </motion.button>

                <a href="#features" className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-900/65 hover:text-neutral-900 transition-colors">
                  Learn more
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </a>
              </div>

              {/* Trust pills */}
              <div className="flex items-center gap-5 flex-wrap">
                {['Fast delivery', '100+ restaurants', 'Secure checkout'].map(item => (
                  <div key={item} className="flex items-center gap-1.5 text-[0.78rem] font-semibold text-black/55">
                    <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-black/10 text-[0.6rem] font-bold">✓</span>
                    {item}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Receipt */}
            <motion.div
              initial={{ opacity: 0, x: 28 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex justify-center items-center relative"
            >
              <div className="absolute inset-[-30px] rounded-full bg-[radial-gradient(ellipse,rgba(201,168,0,0.22)_0%,transparent_70%)]" />
              <motion.div
                whileHover={{ rotate: 0 }}
                className="relative bg-white rounded-sm shadow-[0_20px_60px_rgba(0,0,0,0.18),0_4px_12px_rgba(0,0,0,0.08)] p-8 w-[300px] font-mono"
                style={{ rotate: '1.8deg' }}
              >
                {/* Jagged top */}
                <div
                  className="absolute -top-2 left-0 right-0 h-3 bg-white"
                  style={{
                    WebkitMaskImage: 'radial-gradient(circle at 10px 0%, transparent 8px, black 8px)',
                    maskImage: 'radial-gradient(circle at 10px 0%, transparent 8px, black 8px)',
                    WebkitMaskSize: '20px 100%',
                    maskSize: '20px 100%',
                    WebkitMaskRepeat: 'repeat-x',
                    maskRepeat: 'repeat-x',
                  }}
                />

                {/* Portal badge */}
                <div className="absolute -top-3.5 -right-4 bg-neutral-900 text-white text-[0.6rem] font-bold tracking-widest uppercase px-2.5 py-1.5 rounded-lg shadow-lg -rotate-[4deg]">
                  WEB ORDER
                </div>

                {/* Receipt header */}
                <div className="text-center border-b-2 border-dashed border-neutral-300 pb-5 mb-4">
                  <img src="/munchiesLogo.png" alt="Munchies" className="h-8 w-auto mx-auto mb-2" />
                  <p className="font-semibold text-[0.75rem]">Neighbourhood Munchies</p>
                  <p className="text-[0.6rem] text-neutral-500 mt-0.5">Fast &amp; Easy Food Delivery</p>
                </div>

                {/* Meta */}
                {[['Order #:', 'MUN-2026-5871'], ['Date:', 'Apr 20, 2026'], ['Channel:', 'Web Portal']].map(([k, v]) => (
                  <div key={k} className="flex justify-between text-[0.6rem] mb-1">
                    <span className="text-neutral-500">{k}</span>
                    <span className="font-semibold text-neutral-800">{v}</span>
                  </div>
                ))}

                <hr className="border-dashed border-neutral-300 my-3" />

                {/* Items */}
                {[
                  { name: 'Margherita Pizza', desc: 'Large · Extra Cheese', price: '$124.00' },
                  { name: 'Classic Burger', desc: 'With Fries', price: '$85.50' },
                  { name: 'Soft Drink', desc: 'Large', price: '$7.99' },
                ].map(item => (
                  <div key={item.name} className="flex justify-between items-start mb-2.5">
                    <div>
                      <p className="text-[0.68rem] font-semibold">{item.name}</p>
                      <p className="text-[0.55rem] text-neutral-400">{item.desc}</p>
                    </div>
                    <span className="text-[0.68rem] font-semibold ml-2">{item.price}</span>
                  </div>
                ))}

                <hr className="border-dashed border-neutral-300 my-3" />

                {[['Subtotal:', '$217.49'], ['Delivery Fee:', '$25.00']].map(([k, v]) => (
                  <div key={k} className="flex justify-between text-[0.6rem] text-neutral-500 mb-1">
                    <span>{k}</span><span className="text-neutral-800">{v}</span>
                  </div>
                ))}

                <div className="flex justify-between items-baseline border-t-2 border-neutral-900 mt-2 pt-2">
                  <span className="text-[0.75rem] font-bold">TOTAL</span>
                  <span className="text-[1.35rem] font-bold">$242.49</span>
                </div>

                {/* Footer */}
                <div className="border-t-2 border-dashed border-neutral-300 mt-3 pt-3 text-center text-[0.58rem] text-neutral-400 leading-relaxed">
                  <strong className="block text-neutral-800 text-[0.65rem] mb-0.5">Thank you for your order!</strong>
                  Estimated delivery: 25–30 min<br />
                  Order online at neighbourhoodmunchies.com
                </div>
              </motion.div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Scroll indicator */}
      <div className="bg-gradient-to-br from-[#e8cc45] via-[#f5d96b] to-[#fff3b0] flex justify-center pb-8 -mt-px">
        <div className="w-2.5 h-2.5 rounded-full bg-black/25 animate-scroll-bounce" />
      </div>
    </>
  )
}
